"use client";

import { useState } from "react";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const filterChips = [{
  value: "all",
  label: "All Matches"
}, {
  value: "november",
  label: "November"
}, {
  value: "december",
  label: "December"
}, {
  value: "pro-league",
  label: "Pro League"
}, {
  value: "community",
  label: "Community Cup"
}];
const fallbackWorkshops = [{
  id: "ws-1",
  title: "Pixel Force Pro League: Winter Qualifier",
  excerpt: "The ultimate showdown for regional dominance. Watch our top-tier squads battle for the championship title in this high-stakes tournament.",
  date: "November 14, 2025 — GLOBAL ARENA, ONLINE",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/tournament_winter_qualifier.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/tournament_winter_qualifier.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/tournament_winter_qualifier.webp-wvc-sizes",
  price: "Free Entry",
  capacity: "12 of 16 slots filled",
  tags: ["Pro League", "Live Stream", "High Stakes"],
  link: "/TheGridPage"
}, {
  id: "ws-2",
  title: "Community Clash: Tactical Ops Edition",
  excerpt: "Join the community for a night of intense tactical gameplay. Open to all skill levels, featuring custom maps and exclusive rewards.",
  date: "November 28, 2025 — PIXEL HUB, DISCORD",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/tournament_community_clash.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/tournament_community_clash.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/tournament_community_clash.png-wvc-sizes",
  price: "Free Entry",
  capacity: "8 of 14 slots filled",
  tags: ["Community", "Discord", "Custom Maps"],
  link: "/TheNexusPage"
}, {
  id: "ws-3",
  title: "Elite Invitational: Grand Finals",
  excerpt: "The culmination of the season. Witness the best players in the world compete for the Pixel Force trophy and massive prize pools.",
  date: "December 06, 2025 — CYBER STADIUM, TOKYO",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/tournament_grand_finals.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/tournament_grand_finals.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/tournament_grand_finals.png-wvc-sizes",
  price: "Spectator Pass",
  capacity: "6 of 12 slots filled",
  tags: ["Invitational", "Live Event", "Grand Prize"],
  link: "/TheHolodeckPage"
}];
function WorkshopList({
  selectedFilter
}: {
  selectedFilter: string;
  setSelectedFilter: (val: string) => void;
}) {
  const {
    posts,
    loading,
    error,
    currentPage,
    totalPages,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    setPage
  } = usePosts() as any;
  const allItems = !loading && !error && posts && posts.length > 0 ? posts.map((p: any, idx: number) => {
    const fb = fallbackWorkshops[idx % fallbackWorkshops.length];
    return {
      id: p.id,
      title: p.title || fb.title,
      excerpt: (p.excerpt || "").replace(/<[^>]*>/g, "") || fb.excerpt,
      date: fb.date,
      image: p.featuredImage || fb.image,
      srcset: p.featuredImage ? undefined : fb.srcset,
      sizes: p.featuredImage ? undefined : fb.sizes,
      price: fb.price,
      capacity: fb.capacity,
      tags: fb.tags,
      link: p.link || "#",
      isWpPost: !!p.featuredImage
    };
  }) : fallbackWorkshops.map(w => ({
    ...w,
    isWpPost: false
  }));
  const items = selectedFilter === "all" ? allItems : allItems.filter((item: any) => {
    const matchesTag = item.tags.some((tag: string) => tag.toLowerCase().includes(selectedFilter.toLowerCase().replace("-", " ")));
    const matchesMonth = item.date.toLowerCase().includes(selectedFilter.toLowerCase());
    return matchesTag || matchesMonth;
  });
  if (loading) {
    return <div className="flex flex-col gap-8">
        {[0, 1, 2].map(i => <div key={i} data-index={i} className="rounded-lg border border-border bg-muted/60 p-10 animate-pulse h-80" />)}
      </div>;
  }
  const pageNumbers: number[] = [];
  const safeTotalPages = Math.max(1, totalPages || 1);
  const safeCurrent = currentPage || 1;
  for (let i = 1; i <= safeTotalPages; i++) pageNumbers.push(i);
  return <>
      <div className="flex flex-col gap-8">
        {items.map((workshop: any, i: number) => <article data-index={i} key={workshop.id} className="group rounded-lg border border-border bg-card p-6 sm:p-8 lg:p-10 transition-all duration-500 hover:shadow-lg">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
              <div className="lg:col-span-4">
                <div className="aspect-square overflow-hidden rounded-lg bg-muted">
                  {workshop.isWpPost ? <img src={workshop.image} alt={workshop.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /> : <img src={workshop.image} data-wvc-srcset={workshop.srcset} data-wvc-sizes={workshop.sizes} alt={workshop.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />}
                </div>
              </div>

              <div className="lg:col-span-5 flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <span className="h-px w-8 bg-primary" />
                  <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                    {workshop.date}
                  </span>
                </div>

                <h3 className="font-default text-2xl sm:text-3xl lg:text-4xl font-semibold leading-tight text-foreground">
                  {workshop.title}
                </h3>

                <p className="text-base text-muted-foreground leading-relaxed line-clamp-2">
                  {workshop.excerpt}
                </p>

                <div className="flex flex-wrap gap-2 pt-2">
                  {workshop.tags.map((tag: string, ti: number) => <Badge data-index={ti} key={tag} variant="secondary" className="rounded-lg bg-primary/10 text-primary border border-primary/20 font-mono text-[10px] uppercase tracking-wider px-3 py-1">
                      {tag}
                    </Badge>)}
                </div>
              </div>

              <div className="lg:col-span-3 flex flex-col gap-4 lg:items-end lg:text-right">
                <div className="flex flex-col gap-2 lg:items-end">
                  <span className="font-default italic text-3xl lg:text-4xl font-semibold text-foreground">
                    {workshop.price}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    {workshop.capacity}
                  </span>
                </div>

                <Link to={workshop.link || "#"} className="w-full lg:w-auto">
                  <Button size="lg" className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs uppercase tracking-widest px-6 w-full lg:w-auto">
                    Join Match
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </article>)}
        {items.length === 0 && <div className="text-center py-20 border border-dashed border-border rounded-lg">
            <p className="text-muted-foreground font-mono text-sm uppercase tracking-widest">No matches found for this filter.</p>
          </div>}
      </div>

      {safeTotalPages > 1 && items.length > 0 && <div className="mt-16 flex items-center justify-center gap-2">
          <Button variant="ghost" size="icon" onClick={prevPage} disabled={!hasPrev} className="rounded-lg h-9 w-9 text-foreground hover:bg-accent disabled:opacity-30" aria-label="Previous page">
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {pageNumbers.map((n, i) => <Button data-index={i} key={n} variant="ghost" size="sm" onClick={() => setPage(n)} className={`rounded-lg h-9 min-w-9 font-mono text-xs ${n === safeCurrent ? "bg-primary text-primary-foreground hover:bg-primary/90" : "text-foreground hover:bg-accent"}`}>
              {String(n).padStart(2, "0")}
            </Button>)}

          <Button variant="ghost" size="icon" onClick={nextPage} disabled={!hasNext} className="rounded-lg h-9 w-9 text-foreground hover:bg-accent disabled:opacity-30" aria-label="Next page">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>}
    </>;
}
export default function TournamentSchedule() {
  const [selectedFilter, setSelectedFilter] = useState("all");
  const wp_query_params = {
    post_type: "post",
    per_page: 6,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="3389" id="tournament-schedule" className="relative bg-background text-foreground py-24 lg:py-32 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="mb-16 lg:mb-20">
          <div className="flex items-center gap-3 mb-8">
            <span className="h-px w-12 bg-primary" />
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-primary">
              Pixel Force — Tournament Schedule
            </span>
          </div>

          <div className="grid grid-grid-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <h2 className="lg:col-span-8 font-default text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-foreground">
              Dominate the arena. Join the next generation of esports.
            </h2>

            <div className="lg:col-span-4 lg:pt-4">
              <p className="text-base lg:text-lg text-muted-foreground leading-relaxed">
                From community-driven skirmishes to high-stakes professional leagues, Pixel Force is your command center for competitive gaming. Register now and secure your spot on the leaderboard.
              </p>
            </div>
          </div>
        </div>

        <div className="sticky top-4 z-20 mb-12 lg:mb-16">
          <div className="rounded-lg border border-border bg-background/85 backdrop-blur-md px-4 py-3 shadow-md">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground px-2">
                Filter
              </span>
              <ToggleGroup type="single" value={selectedFilter} onValueChange={value => {
              if (value) setSelectedFilter(value);
            }} className="flex flex-wrap gap-2">
                {filterChips.map((chip, i) => <ToggleGroupItem data-index={i} key={chip.value} value={chip.value} className="rounded-lg border border-border bg-transparent px-4 py-2 text-xs font-mono uppercase tracking-wider text-foreground hover:bg-primary/10 data-[state=on]:bg-primary/20 data-[state=on]:text-primary data-[state=on]:border-primary/40 transition-all">
                    {chip.label}
                  </ToggleGroupItem>)}
              </ToggleGroup>
            </div>
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 6,
        order: "desc",
        orderby: "date"
      }}>
          <WorkshopList selectedFilter={selectedFilter} setSelectedFilter={setSelectedFilter} />
        </PostsProvider>
      </div>
    </section>;
}